import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { getBrandById, getOrganizerById, updateMatchStatus } from '../../services/dataService';
import { getOrCreateConversation, sendMessage } from '../../services/chatService';
import { Match } from '../../services/matchingService';
import { Message } from '../../services/chatService';
import { Button } from '../../components/Button';
import { CheckIcon, XIcon, MessageSquareIcon, ArrowLeftIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
import { ContractForm, ContractDetails } from '../../components/ContractForm';
export function MatchDetails() {
  const {
    matchId
  } = useParams<{
    matchId: string;
  }>();
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [brand, setBrand] = useState<any | null>(null);
  const [organizer, setOrganizer] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<'brand' | 'organizer'>('brand');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState<any | null>(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [contract, setContract] = useState<any | null>(null);
  useEffect(() => {
    const loadData = async () => {
      if (!matchId || !currentUser) return;
      // Get match data
      const matches = JSON.parse(localStorage.getItem('matches') || '[]') as Match[];
      const foundMatch = matches.find(m => m.id === matchId);
      if (!foundMatch) {
        navigate('/dashboard');
        return;
      }
      setMatch(foundMatch);
      // Get brand and organizer data
      const brandData = getBrandById(foundMatch.brandId);
      const organizerData = getOrganizerById(foundMatch.organizerId);
      setBrand(brandData);
      setOrganizer(organizerData);
      // Determine user type
      if (brandData && brandData.userId === currentUser.id) {
        setUserType('brand');
      } else if (organizerData && organizerData.userId === currentUser.id) {
        setUserType('organizer');
      }
      // Get conversation
      if (brandData && organizerData) {
        const conv = getOrCreateConversation(brandData.id, organizerData.id);
        setConversation(conv);
        setMessages(conv.messages);
      }
      // Check if there's an existing contract
      const contracts = JSON.parse(localStorage.getItem('contracts') || '[]');
      const existingContract = contracts.find((c: any) => c.matchId === matchId);
      if (existingContract) {
        setContract(existingContract);
      }
      setLoading(false);
    };
    loadData();
  }, [matchId, currentUser, navigate]);
  const handleStatusChange = (status: 'accepted' | 'rejected') => {
    if (!match) return;
    const updatedMatch = updateMatchStatus(match.id, status);
    setMatch(updatedMatch);
  };
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversation || !newMessage.trim() || !currentUser) return;
    sendMessage(conversation.id, currentUser.id, userType, newMessage);
    setNewMessage('');
    // Refresh messages
    const conv = JSON.parse(localStorage.getItem('conversations') || '[]').find((c: any) => c.id === conversation.id);
    if (conv) {
      setMessages(conv.messages);
    }
  };
  const handleContractCreated = (contractData: any) => {
    setContract(contractData);
    setShowContractForm(false);
  };
  if (loading) {
    return <DashboardLayout userType={userType} data-id="element-2065">
        <div className="flex justify-center items-center h-64" data-id="element-2066">
          <div className="text-gray-500" data-id="element-2067">Laddar...</div>
        </div>
      </DashboardLayout>;
  }
  if (!match || !brand || !organizer) {
    return <DashboardLayout userType={userType} data-id="element-2068">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-2069">
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2070">
            Match hittades inte
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-2071">
            Matchen du letar efter finns inte eller så har du inte behörighet
            att visa den.
          </p>
          <button onClick={() => navigate(-1)} className="flex items-center text-indigo-600 hover:text-indigo-800" data-id="element-2072">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-2073" />
            Gå tillbaka
          </button>
        </div>
      </DashboardLayout>;
  }
  return <DashboardLayout userType={userType} data-id="element-2074">
      <div className="mb-6" data-id="element-2075">
        <button onClick={() => navigate(-1)} className="flex items-center text-indigo-600 hover:text-indigo-800" data-id="element-2076">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-2077" />
          Tillbaka till matchningar
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-2078">
        <div className="flex justify-between items-center mb-4" data-id="element-2079">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2080">
            {userType === 'brand' ? organizer.eventName : brand.companyName}
          </h1>
          <div className={`px-3 py-1 inline-flex items-center rounded-full ${match.score >= 80 ? 'bg-green-100 text-green-800' : match.score >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-2081">
            <span className="text-sm font-medium" data-id="element-2082">{match.score}% Match</span>
          </div>
        </div>
        {/* Match status actions */}
        {match.status === 'pending' && <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6" data-id="element-2083">
            <div className="flex items-start" data-id="element-2084">
              <div className="flex-shrink-0" data-id="element-2085">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-2086" />
              </div>
              <div className="ml-3" data-id="element-2087">
                <h3 className="text-sm font-medium text-yellow-800" data-id="element-2088">
                  {userType === 'brand' ? 'Ny sponsringsmöjlighet' : 'Ny sponsormatch'}
                </h3>
                <div className="mt-2 text-sm text-yellow-700" data-id="element-2089">
                  <p data-id="element-2090">
                    {userType === 'brand' ? 'Du har matchats med detta event baserat på dina preferenser och krav.' : 'Du har matchats med detta varumärke baserat på dina eventdetaljer och deras sponsringskriterier.'}
                  </p>
                </div>
                <div className="mt-4" data-id="element-2091">
                  <div className="flex space-x-3" data-id="element-2092">
                    <Button variant="primary" onClick={() => handleStatusChange('accepted')} className="flex items-center" data-id="element-2093">
                      <CheckIcon className="h-4 w-4 mr-1" data-id="element-2094" />
                      Acceptera match
                    </Button>
                    <Button variant="secondary" onClick={() => handleStatusChange('rejected')} className="flex items-center" data-id="element-2095">
                      <XIcon className="h-4 w-4 mr-1" data-id="element-2096" />
                      Avböj
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {match.status === 'accepted' && <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6" data-id="element-2097">
            <div className="flex items-start" data-id="element-2098">
              <div className="flex-shrink-0" data-id="element-2099">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-2100" />
              </div>
              <div className="ml-3" data-id="element-2101">
                <h3 className="text-sm font-medium text-green-800" data-id="element-2102">
                  Match accepterad
                </h3>
                <div className="mt-2 text-sm text-green-700" data-id="element-2103">
                  <p data-id="element-2104">
                    {userType === 'brand' ? 'Du har accepterat denna sponsringsmöjlighet. Använd meddelandesystemet nedan för att diskutera detaljer.' : 'Detta varumärke har bekräftats som sponsor för ditt event. Använd meddelandesystemet nedan för att diskutera detaljer.'}
                  </p>
                </div>
                {!contract && match.status === 'accepted' && <div className="mt-4" data-id="element-2105">
                    <Button variant="outline" onClick={() => setShowContractForm(true)} className="flex items-center" data-id="element-2106">
                      <FileTextIcon className="h-4 w-4 mr-1" data-id="element-2107" />
                      Skapa sponsringsavtal
                    </Button>
                  </div>}
              </div>
            </div>
          </div>}
        {match.status === 'rejected' && <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6" data-id="element-2108">
            <div className="flex items-start" data-id="element-2109">
              <div className="flex-shrink-0" data-id="element-2110">
                <XIcon className="h-5 w-5 text-red-400" data-id="element-2111" />
              </div>
              <div className="ml-3" data-id="element-2112">
                <h3 className="text-sm font-medium text-red-800" data-id="element-2113">
                  Match avböjd
                </h3>
                <div className="mt-2 text-sm text-red-700" data-id="element-2114">
                  <p data-id="element-2115">
                    {userType === 'brand' ? 'Du har avböjt denna sponsringsmöjlighet.' : 'Detta varumärke har avböjt att sponsra ditt event.'}
                  </p>
                </div>
              </div>
            </div>
          </div>}
        {/* Match reasons */}
        <div className="mb-6" data-id="element-2116">
          <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-2117">
            Varför ni matchade
          </h2>
          <ul className="space-y-2" data-id="element-2118">
            {match.matchReasons.map((reason, index) => <li key={index} className="flex items-center" data-id="element-2119">
                <div className="bg-indigo-100 rounded-full p-1 mr-2" data-id="element-2120">
                  <CheckIcon className="h-4 w-4 text-indigo-600" data-id="element-2121" />
                </div>
                <span className="text-gray-700" data-id="element-2122">{reason}</span>
              </li>)}
          </ul>
        </div>
        {/* Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2123">
          {userType === 'brand' ? <div data-id="element-2124">
              <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-2125">
                Eventdetaljer
              </h2>
              <div className="space-y-4" data-id="element-2126">
                <div data-id="element-2127">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2128">
                    Eventnamn
                  </h3>
                  <p className="text-gray-900" data-id="element-2129">{organizer.eventName}</p>
                </div>
                <div data-id="element-2130">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2131">
                    Eventbeskrivning
                  </h3>
                  <p className="text-gray-900" data-id="element-2132">{organizer.elevatorPitch}</p>
                </div>
                <div data-id="element-2133">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2134">Datum</h3>
                  <p className="text-gray-900" data-id="element-2135">
                    {new Date(organizer.eventDate).toLocaleDateString()}
                  </p>
                </div>
                <div data-id="element-2136">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2137">Plats</h3>
                  <p className="text-gray-900" data-id="element-2138">{organizer.location}</p>
                </div>
                <div data-id="element-2139">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2140">
                    Publikstorlek
                  </h3>
                  <p className="text-gray-900" data-id="element-2141">
                    {organizer.attendeeCount === 'under_100' ? 'Under 100' : organizer.attendeeCount === '100_500' ? '100 - 500' : organizer.attendeeCount === '500_1000' ? '500 - 1 000' : organizer.attendeeCount === '1000_5000' ? '1 000 - 5 000' : organizer.attendeeCount === '5000_plus' ? '5 000+' : 'Ej angivet'}
                  </p>
                </div>
                <div data-id="element-2142">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2143">
                    Publikbeskrivning
                  </h3>
                  <p className="text-gray-900" data-id="element-2144">
                    {organizer.audienceDescription}
                  </p>
                </div>
                <div data-id="element-2145">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2146">
                    Kontaktinformation
                  </h3>
                  <p className="text-gray-900" data-id="element-2147">
                    {organizer.contactName}
                    <br data-id="element-2148" />
                    {organizer.email}
                    <br data-id="element-2149" />
                    {organizer.phone}
                  </p>
                </div>
                <div data-id="element-2150">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2151">Adress</h3>
                  <p className="text-gray-900" data-id="element-2152">
                    {organizer.address}
                    <br data-id="element-2153" />
                    {organizer.postalCode} {organizer.city}
                  </p>
                </div>
              </div>
            </div> : <div data-id="element-2154">
              <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-2155">
                Varumärkesdetaljer
              </h2>
              <div className="space-y-4" data-id="element-2156">
                <div data-id="element-2157">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2158">
                    Företagsnamn
                  </h3>
                  <p className="text-gray-900" data-id="element-2159">{brand.companyName}</p>
                </div>
                <div data-id="element-2160">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2161">Produkt</h3>
                  <p className="text-gray-900" data-id="element-2162">{brand.productName}</p>
                </div>
                <div data-id="element-2163">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2164">
                    Produktbeskrivning
                  </h3>
                  <p className="text-gray-900" data-id="element-2165">{brand.productDescription}</p>
                </div>
                <div data-id="element-2166">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2167">Bransch</h3>
                  <p className="text-gray-900" data-id="element-2168">
                    {brand.industry === 'food_beverage' ? 'Livsmedel & Dryck' : brand.industry === 'beauty_cosmetics' ? 'Skönhet & Kosmetika' : brand.industry === 'health_wellness' ? 'Hälsa & Välmående' : brand.industry === 'tech' ? 'Teknik' : brand.industry === 'fashion' ? 'Mode & Kläder' : brand.industry === 'home_goods' ? 'Hem & Inredning' : brand.industry === 'sports_fitness' ? 'Sport & Fitness' : brand.industry === 'entertainment' ? 'Underhållning' : 'Annat'}
                  </p>
                </div>
                <div data-id="element-2169">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2170">
                    Sponsringstyp
                  </h3>
                  <p className="text-gray-900" data-id="element-2171">
                    {brand.sponsorshipType.map((type: string) => type === 'product_sampling' ? 'Produktprovning' : type === 'financial_sponsorship' ? 'Finansiell sponsring' : type === 'in_kind_goods' ? 'Varor' : type === 'merchandise' ? 'Merchandise' : type === 'experience' ? 'Varumärkesupplevelse' : type).join(', ')}
                  </p>
                </div>
                <div data-id="element-2172">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2173">
                    Kontaktinformation
                  </h3>
                  <p className="text-gray-900" data-id="element-2174">
                    {brand.contactName}
                    <br data-id="element-2175" />
                    {brand.email}
                    <br data-id="element-2176" />
                    {brand.phone}
                  </p>
                </div>
                <div data-id="element-2177">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2178">Adress</h3>
                  <p className="text-gray-900" data-id="element-2179">
                    {brand.address}
                    <br data-id="element-2180" />
                    {brand.postalCode} {brand.city}
                  </p>
                </div>
              </div>
            </div>}
          <div data-id="element-2181">
            <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-2182">
              {userType === 'brand' ? 'Vad de behöver' : 'Vad de erbjuder'}
            </h2>
            {userType === 'brand' ? <div className="space-y-4" data-id="element-2183">
                <div data-id="element-2184">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2185">
                    Sponsringsbehov
                  </h3>
                  <p className="text-gray-900" data-id="element-2186">{organizer.sponsorshipNeeds}</p>
                </div>
                <div data-id="element-2187">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2188">
                    Erbjudanden för sponsorer
                  </h3>
                  <p className="text-gray-900" data-id="element-2189">
                    {organizer.offeringTypes.map((type: string) => type === 'brand_visibility' ? 'Varumärkessynlighet' : type === 'content_creation' ? 'Innehållsskapande' : type === 'lead_generation' ? 'Leadgenerering' : type === 'product_sampling' ? 'Produktprovning' : type === 'product_feedback' ? 'Produktfeedback' : type === 'merchandise_sales' ? 'Merchandise-försäljning' : type).join(', ')}
                  </p>
                </div>
                {organizer.brandVisibility && <div data-id="element-2190">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2191">
                      Detaljer om varumärkessynlighet
                    </h3>
                    <p className="text-gray-900" data-id="element-2192">{organizer.brandVisibility}</p>
                  </div>}
                {organizer.bonusValueDetails && <div data-id="element-2193">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2194">
                      Ytterligare värde
                    </h3>
                    <p className="text-gray-900" data-id="element-2195">
                      {organizer.bonusValueDetails}
                    </p>
                  </div>}
              </div> : <div className="space-y-4" data-id="element-2196">
                <div data-id="element-2197">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2198">
                    Marknadsföringsmål
                  </h3>
                  <p className="text-gray-900" data-id="element-2199">{brand.marketingGoals}</p>
                </div>
                <div data-id="element-2200">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2201">
                    Budgetintervall
                  </h3>
                  <p className="text-gray-900" data-id="element-2202">
                    {brand.budget === 'under_10000' ? 'Under 10 000 kr' : brand.budget === '10000_50000' ? '10 000 - 50 000 kr' : brand.budget === '50000_100000' ? '50 000 - 100 000 kr' : brand.budget === '100000_250000' ? '100 000 - 250 000 kr' : brand.budget === '250000_plus' ? '250 000 kr+' : 'Ej angivet'}
                  </p>
                </div>
                <div data-id="element-2203">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-2204">
                    Tillgänglig kvantitet
                  </h3>
                  <p className="text-gray-900" data-id="element-2205">
                    {brand.productQuantity || 'Ej angivet'}
                  </p>
                </div>
                {brand.hasTestPanels === 'yes' && <div data-id="element-2206">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-2207">
                      Testpaneler & Samplingaktiviteter
                    </h3>
                    <p className="text-gray-900" data-id="element-2208">{brand.testPanelDetails}</p>
                  </div>}
              </div>}
          </div>
        </div>
      </div>
      {/* Contract section */}
      {showContractForm && match.status === 'accepted' && <div className="mb-6" data-id="element-2209">
          <ContractForm brandName={brand.companyName} organizerName={organizer.organizerName} eventName={organizer.eventName} matchId={match.id} onContractCreated={handleContractCreated} data-id="element-2210" />
        </div>}
      {contract && <div className="mb-6" data-id="element-2211">
          <ContractDetails contract={contract} data-id="element-2212" />
        </div>}
      {/* Messaging */}
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-2213">
        <div className="flex items-center mb-4" data-id="element-2214">
          <MessageSquareIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-2215" />
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2216">Meddelanden</h2>
        </div>
        <div className="border rounded-lg mb-4" data-id="element-2217">
          <div className="h-64 overflow-y-auto p-4 space-y-3" data-id="element-2218">
            {messages.length === 0 ? <div className="text-center text-gray-500 py-8" data-id="element-2219">
                Inga meddelanden än. Starta konversationen!
              </div> : messages.map(message => <div key={message.id} className={`flex ${message.senderType === userType ? 'justify-end' : 'justify-start'}`} data-id="element-2220">
                  <div className={`max-w-3/4 rounded-lg p-3 ${message.senderType === 'ai' ? 'bg-gray-100 text-gray-800' : message.senderType === userType ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-800'}`} data-id="element-2221">
                    {message.senderType === 'ai' && <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-2222">
                        AI Assistent
                      </div>}
                    <p className="text-sm" data-id="element-2223">{message.content}</p>
                    <div className="text-xs mt-1 text-right opacity-70" data-id="element-2224">
                      {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </div>
                  </div>
                </div>)}
          </div>
          <form onSubmit={handleSendMessage} className="border-t p-3 flex" data-id="element-2225">
            <input type="text" placeholder="Skriv ett meddelande..." className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" value={newMessage} onChange={e => setNewMessage(e.target.value)} disabled={match.status === 'rejected'} data-id="element-2226" />
            <Button type="submit" variant="primary" className="ml-2" disabled={!newMessage.trim() || match.status === 'rejected'} data-id="element-2227">
              Skicka
            </Button>
          </form>
        </div>
        <div className="text-xs text-gray-500" data-id="element-2228">
          <p data-id="element-2229">
            Behöver du hjälp med din konversation? Vår AI-assistent kommer att
            föreslå hjälpsamma svar när du ställer frågor.
          </p>
        </div>
      </div>
    </DashboardLayout>;
}
function AlertCircleIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2230">
      <circle cx="12" cy="12" r="10" data-id="element-2231" />
      <line x1="12" y1="8" x2="12" y2="12" data-id="element-2232" />
      <line x1="12" y1="16" x2="12.01" y2="16" data-id="element-2233" />
    </svg>;
}